import { Component, OnInit, AfterViewInit, signal, inject, OnDestroy, ViewChild, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, Observable, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';

import { AddFormComponent, FormField, TableSection, ActionButton } from '@shared/components/add-form/add-form.component';
import { DataTableComponent, TableColumn } from '@shared/components/data-table/data-table.component';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import {
  OUTBOUND_DELIVERY_ORDER_ADD_FORM_CONFIG,
  OUTBOUND_DELIVERY_ORDER_PRODUCT_TABLE_SECTION,
  OUTBOUND_DELIVERY_ORDER_ADD_HEADER_ACTIONS,
  TransferOrderOption,
  TRANSFER_ORDER_SELECTION_TABLE_COLUMNS,
} from './outbound-delivery-order-add.config';
import { OutboundDeliveryItem } from '../../stock-delivery.config';
import { LogicTransferService } from '../../../transfer/logic/logic-transfer.service';
import { LogicTransferItem } from '../../../transfer/logic/logic-transfer.config';
import { StockDeliveryService } from '../../stock-delivery.service';
import { BASE_PATH, ResultRecordControllerService, OutboundDeliveryOrderControllerService } from '@app/api';
import { OutboundDeliveryOrderService } from '@app/api/stock-service';
import { environment } from '@environments/environment';
import { PurchaseReturnOrderService } from '@app/feature/center-business/procurement/purchase-return-order/purchase-return-order.service';
import { PaginatedDropdownLoadParams } from '@shared/utils/paginated-dropdown.util';

@Component({
  selector: 'app-outbound-delivery-order-add',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ToastModule,
    AddFormComponent,
    DataTableComponent,
    DialogModule,
    ButtonModule,
    InputTextModule,
  ],
  providers: [
    MessageService,
    {
      provide: BASE_PATH,
      useValue: environment.stockURL,
    },
    ResultRecordControllerService,
    OutboundDeliveryOrderControllerService,
    OutboundDeliveryOrderService,
    StockDeliveryService,
    PurchaseReturnOrderService,
  ],
  templateUrl: './outbound-delivery-order-add.component.html',
  styleUrl: './outbound-delivery-order-add.component.css',
})
export class OutboundDeliveryOrderAddComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('addForm') addFormComponent!: AddFormComponent;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private logicTransferService = inject(LogicTransferService);
  private stockDeliveryService = inject(StockDeliveryService);
  private porService = inject(PurchaseReturnOrderService);
  private destroy$ = new Subject<void>();

  // Form configuration
  formFields = signal<FormField[]>(OUTBOUND_DELIVERY_ORDER_ADD_FORM_CONFIG);
  tableSections = signal<TableSection[]>([OUTBOUND_DELIVERY_ORDER_PRODUCT_TABLE_SECTION]);
  headerActions = signal<ActionButton[]>(OUTBOUND_DELIVERY_ORDER_ADD_HEADER_ACTIONS);

  // Computed form fields - disable outType when creating from other pages
  updatedFormFields = computed(() => {
    const isFromOtherPage = this.isCreatingFromTO() || this.isCreatingFromPOR() || this.isCreatingFromOtherPage();
    
    return this.formFields().map(field => {
      // Disable outType field when creating from other pages (SO/ODO/etc.)
      if (field.controlName === 'outType' && isFromOtherPage) {
        return {
          ...field,
          disabled: true,
        };
      }
      return field;
    });
  });

  // Data
  productList = signal<OutboundDeliveryItem[]>([]);
  selectedProducts = signal<OutboundDeliveryItem[]>([]);
  loading = signal(false);
  private skipCanDeactivate = false;

  // Transfer Order Selection Dialog
  showTransferOrderDialog = signal(false);
  transferOrderList = signal<TransferOrderOption[]>([]);
  transferOrderColumns = signal<TableColumn[]>(TRANSFER_ORDER_SELECTION_TABLE_COLUMNS);
  selectedTransferOrder = signal<TransferOrderOption | null>(null);

  // Warehouse options - cache from LogicTransferService
  warehouseDataMap = signal<Map<number, { id: number; code: string; name: string }>>(new Map());

  // Track current outType
  currentOutType = signal<string>('transfer-out-stock');

  // Track if creating from TO page (with transferOrderCode in queryParams)
  isCreatingFromTO = signal<boolean>(false);
  
  // Track if creating from POR page (with porId in queryParams)
  isCreatingFromPOR = signal<boolean>(false);
  
  // Track if creating from SO/ODO or other pages (with orderCode or other params in queryParams)
  isCreatingFromOtherPage = signal<boolean>(false);

  // Computed signal to check if sales_order is selected
  isSalesOrderSelected = computed(() => {
    return this.currentOutType() === 'order-sales-out-stock';
  });

  // Computed signal to check if transfer_order is selected
  isTransferOrderSelected = computed(() => {
    return this.currentOutType() === 'transfer-out-stock';
  });
  
  // Computed signal to check if purchase_return_order is selected
  isPurchaseReturnOrderSelected = computed(() => {
    return this.currentOutType() === 'purchase-order-return-out-stock';
  });

  // Track if data has been loaded for each outType
  porOptionsLoaded = signal<boolean>(false);
  salesOrdersLoaded = signal<boolean>(false); // For future use if needed
  
  // Store POR options for quick access when switching outType
  porOptionsList = signal<Array<{ label: string; value: any; id?: number }>>([]);
  
  // Track last selected POR code to prevent duplicate API calls
  private lastSelectedPORCode: string | null = null;
  private isLoadingPORDetails = false;
  
  // Track last selected TO code to prevent duplicate API calls
  private lastSelectedTOCode: string | null = null;
  private isLoadingTODetails = false;


  ngOnInit(): void {
    this.setupFormValueChanges();
    // Load warehouses first for mapping
    this.loadWarehouses();
    
    // Load POR options immediately when entering the page
    this.loadPOROptions();
    
    // Initialize with default outType 'transfer-out-stock'
    this.currentOutType.set('transfer-out-stock');
    
    // Check if porId is passed via queryParams (creating from POR page)
    const porId = this.route.snapshot.queryParamMap.get('porId');
    const porCode = this.route.snapshot.queryParamMap.get('porCode');
    const outType = this.route.snapshot.queryParamMap.get('outType');
    
    if (porId && outType === 'purchase-order-return-out-stock') {
      // Mark as creating from POR page
      this.isCreatingFromPOR.set(true);
      this.isCreatingFromOtherPage.set(true);
      this.currentOutType.set('purchase-order-return-out-stock');
      // Update form fields for purchase return order
      this.updateFormFieldsForPurchaseReturnOrder(true, porCode || '');
      // Set outType in form if form is ready
      setTimeout(() => {
        if (this.addFormComponent?.form) {
          const form = this.addFormComponent.form;
          form.get('outType')?.setValue('purchase-order-return-out-stock', { emitEvent: false });
        }
      }, 0);
      // Don't load here - wait for AfterViewInit to ensure form is ready
    } else {
      // Check if transferOrderCode is passed via queryParams
      const transferOrderCode = this.route.snapshot.queryParamMap.get('transferOrderCode');
      const orderCode = this.route.snapshot.queryParamMap.get('orderCode');
      if (transferOrderCode) {
        // Mark as creating from TO page
        this.isCreatingFromTO.set(true);
        this.isCreatingFromOtherPage.set(true);
        // Set outType to 'transfer-out-stock' (value for transfer_order)
        this.currentOutType.set('transfer-out-stock');
        // Update form fields to show and disable frontRecordCode field as text
        this.updateFormFieldsForTransferOrder(true, transferOrderCode || undefined);
        // Don't load here - wait for AfterViewInit to ensure form is ready
      } else if (orderCode) {
        // Mark as creating from SO/ODO or other pages
        this.isCreatingFromOtherPage.set(true);
        // Update form fields to show and disable frontRecordCode field as text
        this.updateFormFieldsForTransferOrder(true, orderCode || undefined);
        // Don't load here - wait for AfterViewInit to ensure form is ready
      } else {
        // Normal create flow - auto select transfer-out-stock and load TO list
        // Update form fields for default outType (transfer-out-stock)
        this.updateFormFieldsForTransferOrder(false);
        // Set outType in form after form is ready
        setTimeout(() => {
          if (this.addFormComponent?.form) {
            const form = this.addFormComponent.form;
            form.get('outType')?.setValue('transfer-out-stock', { emitEvent: true });
          }
        }, 0);
      }
    }
  }

  ngAfterViewInit(): void {
    // Check if porId is passed via queryParams (creating from POR page)
    const porId = this.route.snapshot.queryParamMap.get('porId');
    const outType = this.route.snapshot.queryParamMap.get('outType');
    
    if (porId && outType === 'purchase-order-return-out-stock' && this.isCreatingFromPOR()) {
      // Wait a bit more for form to be fully initialized
      setTimeout(() => {
        this.loadPORAndFillForm(Number(porId));
      }, 500);
    } else {
      // Check if transferOrderCode is passed via queryParams
      const transferOrderCode = this.route.snapshot.queryParamMap.get('transferOrderCode');
      if (transferOrderCode && this.isCreatingFromTO()) {
        // Wait a bit more for form to be fully initialized
        setTimeout(() => {
          this.loadTransferOrdersAndSelect(transferOrderCode);
        }, 500);
      } else if (!this.isCreatingFromOtherPage()) {
        // Normal create flow - ensure TO list is loaded and form fields are updated
        // Wait for form to be fully initialized
        setTimeout(() => {
          // Update form fields to show frontRecordCode with paginated options.
          this.updateFormFieldsForTransferOrder(false);
        }, 500);
      }
    }
  }

  private loadWarehouses(): void {
    this.logicTransferService.loadAllWarehouses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (warehouses) => {
          // Create a Map for quick lookup by ID
          const warehouseMap = new Map<number, { id: number; code: string; name: string }>();
          warehouses.forEach((wh) => {
            if (wh.id !== undefined && wh.id !== null) {
              warehouseMap.set(wh.id, {
                id: wh.id,
                code: wh.code || '',
                name: wh.name || wh.code || '',
              });
            }
          });
          this.warehouseDataMap.set(warehouseMap);
        },
        error: (error) => {
          // Error loading warehouses - continue without mapping
          this.warehouseDataMap.set(new Map());
        },
      });
  }

  private getWarehouseById(warehouseId: number | string | undefined): { code: string; name: string } | null {
    if (!warehouseId) return null;
    
    const id = typeof warehouseId === 'string' ? parseInt(warehouseId, 10) : warehouseId;
    if (isNaN(id)) return null;
    
    const warehouseMap = this.warehouseDataMap();
    const warehouse = warehouseMap.get(id);
    
    if (warehouse) {
      return {
        code: warehouse.code,
        name: warehouse.name,
      };
    }
    
    return null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupFormValueChanges(): void {
    // This will be handled by AddFormComponent's formValueChanges output
  }

  onFormAction(action: string): void {
    switch (action) {
      case 'save':
        this.onSave();
        break;
      case 'cancel':
        this.onCancel();
        break;
    }
  }

  onFormValueChanges(formData: any): void {
    // Track outType changes
    if (formData?.outType) {
      const previousOutType = this.currentOutType();
      const outTypeChanged = previousOutType !== formData.outType;
      
      if (outTypeChanged) {
        this.currentOutType.set(formData.outType);
        
        // Clear last selected codes when outType changes to avoid conflicts
        this.lastSelectedPORCode = null;
        this.lastSelectedTOCode = null;
        
        // Clear all data (warehouse and table) when outType changes
        this.clearAllData();
        
        // Show/hide reference document field based on outType
        // Don't change field config if creating from TO or POR page (already set in ngOnInit)
        if (!this.isCreatingFromTO() && !this.isCreatingFromPOR()) {
          // Only update form fields if outType changed
          // Data is already loaded in ngOnInit, so no need to call API again
          if (formData.outType === 'order-sales-out-stock') {
            // TODO: Determine if Sales Orders need to be loaded
            // For now, Sales Orders might not need separate API call
            this.updateFormFieldsForSalesOrder();
          } else if (formData.outType === 'transfer-out-stock') {
            // Update form fields - data already loaded in ngOnInit
            this.updateFormFieldsForTransferOrder(false);
          } else if (formData.outType === 'purchase-order-return-out-stock') {
            // Update form fields - data already loaded in ngOnInit
            this.updateFormFieldsForPurchaseReturnOrder(false);
          }
        }
        
        // Don't process frontRecordCode when outType just changed
        // Wait for next form value change with actual frontRecordCode value
        return;
      }
    }

    // Handle frontRecordCode selection based on outType
    // Only process if outType matches and frontRecordCode has a valid value
    if (this.isPurchaseReturnOrderSelected()) {
      // Handle POR code selection
      const porCode = formData?.frontRecordCode;
      if (porCode !== undefined && porCode !== null && porCode !== '') {
        // Only call if POR code actually changed
        if (porCode !== this.lastSelectedPORCode) {
          this.handlePORSelection(porCode);
        }
      } else if (porCode === null || porCode === '') {
        // Clear data when POR is cleared
        this.lastSelectedPORCode = null;
        this.clearPORData();
      }
    } else if (this.isTransferOrderSelected()) {
      // Handle Transfer Order code selection
      const toCode = formData?.frontRecordCode;
      if (toCode !== undefined && toCode !== null && toCode !== '') {
        // Only call if TO code actually changed
        if (toCode !== this.lastSelectedTOCode) {
          this.handleTransferOrderSelection(toCode);
        }
      } else if (toCode === null || toCode === '') {
        // Clear data when Transfer Order is cleared
        this.lastSelectedTOCode = null;
        this.clearTransferOrderData();
      }
    } else if (this.isSalesOrderSelected()) {
      // Handle Sales Order - no special handling needed for now
      // Just clear data if frontRecordCode is cleared
      const salesOrderCode = formData?.frontRecordCode;
      if (salesOrderCode === null || salesOrderCode === '') {
        this.clearAllData();
      }
    }
  }

  private updateFormFieldsForSalesOrder(): void {
    // TODO: Determine what needs to be loaded for Sales Orders
    // Currently, Sales Orders might not need a separate API call
    // If Sales Orders need to be loaded, implement loadSalesOrders() method here
    // For now, we'll keep the field visible but without loading data
    const fields = this.formFields().map((field) => {
      if (field.controlName === 'frontRecordCode') {
        return { 
          ...field, 
          hidden: false,
          disabled: false,
          readonly: false,
          type: 'searchableDropdown' as const,
        };
      }
      return field;
    });
    this.formFields.set(fields);
  }

  private updateFormFieldsForTransferOrder(disableFrontRecordCode: boolean = false, transferOrderCode?: string): void {
    // Update frontRecordCode field to show and optionally disable
    const fields = this.formFields().map((field) => {
      if (field.controlName === 'frontRecordCode') {
        if (disableFrontRecordCode && transferOrderCode) {
          // When creating from TO, change to text type (readonly)
          return { 
            ...field, 
            hidden: false, 
            type: 'text' as const,
            disabled: true,
            readonly: true,
            defaultValue: transferOrderCode,
          };
        } else {
          // Normal mode - keep as searchableDropdown with API paging on scroll.
          return { 
            ...field, 
            hidden: false, 
            disabled: false,
            readonly: false,
            type: 'searchableDropdown' as const,
            options: [],
            paginatedOptions: {
              loadPage: (params: PaginatedDropdownLoadParams) =>
                this.getTransferOrderOptionsPage(params),
            },
          };
        }
      }
      return field;
    });
    this.formFields.set(fields);
    
    // No need to load transfer orders here - already loaded in ngOnInit
  }

  private updateFrontRecordCodeFieldOptions(options: { label: string; value: any }[], defaultValue?: string): void {
    const fields = this.formFields().map((field) => {
      if (field.controlName === 'frontRecordCode') {
        return { 
          ...field, 
          hidden: false, 
          options,
          disabled: this.isCreatingFromTO(), // Keep disabled state if creating from TO page
          defaultValue: defaultValue || field.defaultValue, // Set defaultValue if provided
        }; // Show field and update options
      }
      return field;
    });
    this.formFields.set(fields);
  }

  onTableAction(event: { action: string; data?: any; section?: string }): void {
    // No actions needed - table is read-only
  }

  private updateTableSections(): void {
    const sections = this.tableSections().map((section) => {
      if (section.title === 'outbound_management.sections.item_details') {
        return {
          ...section,
          data: this.productList(),
        };
      }
      return section;
    });
    this.tableSections.set(sections);
  }

  // This method is no longer needed since we use searchableDropdown
  // Keeping for backward compatibility but it won't be called
  onTransferOrderFieldClick(fieldControlName: string): void {
    // No longer needed - using searchableDropdown instead
  }

  onTransferOrderSelect(transferOrder: TransferOrderOption): void {
    this.selectedTransferOrder.set(transferOrder);
    this.showTransferOrderDialog.set(false);
    
    // Load transfer order details and auto-fill warehouse information
    this.loadTransferOrderDetails(transferOrder.id);
  }

  private handleTransferOrderSelection(transferOrderCode: string): void {
    // Prevent duplicate calls
    if (this.isLoadingTODetails) {
      return;
    }
    
    // Update last selected TO code
    this.lastSelectedTOCode = transferOrderCode;
    
    // Resolve by code from API, so behavior stays correct with paginated options.
    this.loadTransferOrderDetailsByCode(transferOrderCode);
  }

  private loadTransferOrderDetailsByCode(transferOrderCode: string): void {
    this.logicTransferService
      .getLogicTransfers(1, 1, {
        status: ['APPROVED', 'IN_PROGRESS'],
        code: transferOrderCode,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const transferOrder = (response.data || []).find(
            (item) => item.code === transferOrderCode,
          );
          if (!transferOrder?.id) {
            return;
          }
          const mapped: TransferOrderOption = {
            id: transferOrder.id,
            code: transferOrder.code || '',
            supportWarehouseName: transferOrder.supportWarehouseName || '',
            receiveWarehouseName: transferOrder.receiveWarehouseName || '',
            status: transferOrder.status || '',
            creationDate: transferOrder.creationDate || '',
          };
          this.selectedTransferOrder.set(mapped);
          this.loadTransferOrderDetails(mapped.id);
        },
      });
  }

  // Clear all data (warehouse and table) - used when changing outType
  private clearAllData(): void {
    // Clear selected orders
    this.selectedTransferOrder.set(null);
    
    // Clear product list
    this.productList.set([]);
    this.updateTableSections();
    
    // Clear form controls
    if (this.addFormComponent?.form) {
      const form = this.addFormComponent.form;
      
      // Clear warehouse codes
      const outWarehouseCodeControl = form.get('outLogicWarehouseCode');
      if (outWarehouseCodeControl) {
        outWarehouseCodeControl.setValue(null, { emitEvent: false });
      }
      const receivingWarehouseCodeControl = form.get('receivingLogicWarehouseCode');
      if (receivingWarehouseCodeControl) {
        receivingWarehouseCodeControl.setValue(null, { emitEvent: false });
      }
      
      // Clear warehouse names if fields exist
      const outWarehouseNameControl = form.get('outLogicWarehouseName');
      if (outWarehouseNameControl) {
        outWarehouseNameControl.setValue(null, { emitEvent: false });
      }
      const receivingWarehouseNameControl = form.get('receivingLogicWarehouseName');
      if (receivingWarehouseNameControl) {
        receivingWarehouseNameControl.setValue(null, { emitEvent: false });
      }
      
      // Clear frontRecordCode when changing outType
      const frontRecordCodeControl = form.get('frontRecordCode');
      if (frontRecordCodeControl) {
        frontRecordCodeControl.setValue(null, { emitEvent: false });
      }
    }
  }

  private clearTransferOrderData(): void {
    this.selectedTransferOrder.set(null);
    if (this.addFormComponent?.form) {
      const form = this.addFormComponent.form;
      // Clear warehouse codes
      const outWarehouseCodeControl = form.get('outLogicWarehouseCode');
      if (outWarehouseCodeControl) {
        outWarehouseCodeControl.setValue(null, { emitEvent: false });
      }
      const receivingWarehouseCodeControl = form.get('receivingLogicWarehouseCode');
      if (receivingWarehouseCodeControl) {
        receivingWarehouseCodeControl.setValue(null, { emitEvent: false });
      }
      // Clear warehouse names if fields exist
      const outWarehouseNameControl = form.get('outLogicWarehouseName');
      if (outWarehouseNameControl) {
        outWarehouseNameControl.setValue(null, { emitEvent: false });
      }
      const receivingWarehouseNameControl = form.get('receivingLogicWarehouseName');
      if (receivingWarehouseNameControl) {
        receivingWarehouseNameControl.setValue(null, { emitEvent: false });
      }
    }
    this.productList.set([]);
    this.updateTableSections();
  }

  private loadTransferOrderDetails(transferOrderId: string): void {
    // Prevent duplicate calls for the same TO ID
    if (this.isLoadingTODetails) {
      return;
    }
    
    this.isLoadingTODetails = true;
    this.logicTransferService.getLogicTransferById(transferOrderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (transferOrder: LogicTransferItem | null) => {
          // Reset loading flag after successful load
          this.isLoadingTODetails = false;
          if (transferOrder && this.addFormComponent) {
            // Use requestAnimationFrame to ensure form is ready
            requestAnimationFrame(() => {
              setTimeout(() => {
                const form = this.addFormComponent?.form;
                if (!form) return;

                // Map warehouses from IDs using warehouse data map
                // Kho xuất (Outbound Warehouse) - map from supportLogicWarehouseId
                const supportWarehouseId = transferOrder.supportLogicWarehouseId;
                if (supportWarehouseId) {
                  const supportWarehouse = this.getWarehouseById(supportWarehouseId);
                  if (supportWarehouse) {
                    const outWarehouseCodeControl = form.get('outLogicWarehouseCode');
                    if (outWarehouseCodeControl) {
                      outWarehouseCodeControl.setValue(supportWarehouse.code, { emitEvent: false });
                    }
                    const outWarehouseNameControl = form.get('outLogicWarehouseName');
                    if (outWarehouseNameControl) {
                      outWarehouseNameControl.setValue(supportWarehouse.name, { emitEvent: false });
                    }
                  } else {
                    // Fallback to code/name from transfer order if mapping fails
                    if (transferOrder.supportWarehouseCode) {
                      const outWarehouseCodeControl = form.get('outLogicWarehouseCode');
                      if (outWarehouseCodeControl) {
                        outWarehouseCodeControl.setValue(transferOrder.supportWarehouseCode, { emitEvent: false });
                      }
                    }
                    if (transferOrder.supportWarehouseName) {
                      const outWarehouseNameControl = form.get('outLogicWarehouseName');
                      if (outWarehouseNameControl) {
                        outWarehouseNameControl.setValue(transferOrder.supportWarehouseName, { emitEvent: false });
                      }
                    }
                  }
                }

                // Kho nhận (Receiving Warehouse) - map from receivingLogicWarehouseId
                const receivingWarehouseId = transferOrder.receivingLogicWarehouseId;
                if (receivingWarehouseId) {
                  const receivingWarehouse = this.getWarehouseById(receivingWarehouseId);
                  if (receivingWarehouse) {
                    const receivingWarehouseCodeControl = form.get('receivingLogicWarehouseCode');
                    if (receivingWarehouseCodeControl) {
                      receivingWarehouseCodeControl.setValue(receivingWarehouse.code, { emitEvent: false });
                    }
                    const receivingWarehouseNameControl = form.get('receivingLogicWarehouseName');
                    if (receivingWarehouseNameControl) {
                      receivingWarehouseNameControl.setValue(receivingWarehouse.name, { emitEvent: false });
                    }
                  } else {
                    // Fallback to code/name from transfer order if mapping fails
                    if (transferOrder.receiveWarehouseCode) {
                      const receivingWarehouseCodeControl = form.get('receivingLogicWarehouseCode');
                      if (receivingWarehouseCodeControl) {
                        receivingWarehouseCodeControl.setValue(transferOrder.receiveWarehouseCode, { emitEvent: false });
                      }
                    }
                    if (transferOrder.receiveWarehouseName) {
                      const receivingWarehouseNameControl = form.get('receivingLogicWarehouseName');
                      if (receivingWarehouseNameControl) {
                        receivingWarehouseNameControl.setValue(transferOrder.receiveWarehouseName, { emitEvent: false });
                      }
                    }
                  }
                }

                // Load product details from transfer order
                if (transferOrder.items && transferOrder.items.length > 0) {
                  const products: OutboundDeliveryItem[] = transferOrder.items.map((detail: any, index: number) => ({
                    id: detail.id ? String(detail.id) : `item-${index}`,
                    serialNumber: index + 1,
                    spuCode: detail.spuCode || '',
                    spuName: detail.spuName || '',
                    skuCode: detail.skuCode || '',
                    skuName: detail.skuName || '',
                    quantity: detail.plannedQty || 0,
                    plannedQty: detail.plannedQty || 0,
                    alreadyOutQty: detail.alreadyOutQty || 0,
                  }));
                  this.productList.set(products);
                  this.updateTableSections();
                } else {
                  // Clear product list if no items
                  this.productList.set([]);
                  this.updateTableSections();
                }
              }, 100);
            });
          }
        },
        error: (error) => {
          // Reset loading flag on error
          this.isLoadingTODetails = false;
          // Error handling - could show message if needed
        },
      });
  }

  onTransferOrderDialogCancel(): void {
    this.showTransferOrderDialog.set(false);
  }

  private getTransferOrderOptionsPage(params: PaginatedDropdownLoadParams): Observable<{
    options: Array<{ label: string; value: string }>;
    pageContentLength: number;
    totalElements: number;
  }> {
    const searchText = params.searchText?.trim();
    return this.logicTransferService
      .getLogicTransfers(params.page, params.size, {
        status: ['APPROVED', 'IN_PROGRESS'],
        code: searchText || undefined,
      })
      .pipe(
        map((response) => {
          const data = response.data || [];
          return {
            options: data
              .filter((item: LogicTransferItem) => !!item.id)
              .map((item: LogicTransferItem) => ({
                label: item.code || '',
                value: item.code || '',
              })),
            pageContentLength: data.length,
            totalElements: response.totalRecords || 0,
          };
        }),
      );
  }

  private loadTransferOrdersAndSelect(transferOrderCode: string): void {
    this.logicTransferService
      .getLogicTransfers(1, 1, {
        status: ['APPROVED', 'IN_PROGRESS'],
        code: transferOrderCode,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const transferOrder = (response.data || []).find(
            (item) => item.code === transferOrderCode,
          );
          if (transferOrder) {
            const mappedTransferOrder: TransferOrderOption = {
              id: transferOrder.id!,
              code: transferOrder.code || '',
              supportWarehouseName: transferOrder.supportWarehouseName || '',
              receiveWarehouseName: transferOrder.receiveWarehouseName || '',
              status: transferOrder.status || '',
              creationDate: transferOrder.creationDate || '',
            };
            // Wait for form to be ready, then fill data
            this.waitForFormAndFillTO(mappedTransferOrder);
          } else {
            // Transfer order not found
            this.messageService.add({
              severity: 'warn',
              summary: this.translateService.instant('common.messages.warning'),
              detail: this.translateService.instant('outbound_management.messages.transfer_order_not_found'),
            });
          }
        },
        error: (error) => {
          // Error loading transfer orders
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('outbound_management.messages.failed_to_load_transfer_orders'),
          });
        },
      });
  }

  private waitForFormAndFillTO(transferOrder: TransferOrderOption, retryCount: number = 0): void {
    const maxRetries = 20;
    const form = this.addFormComponent?.form;
    
    // Set outType first if form is ready
    if (form && form.get('outType')) {
      form.get('outType')?.setValue('transfer-out-stock', { emitEvent: false });
    }

    if (form) {
      // Form is ready, fill data
      this.fillFormFromTO(transferOrder);
    } else if (retryCount < maxRetries) {
      // Form not ready yet, retry after a short delay
      setTimeout(() => {
        this.waitForFormAndFillTO(transferOrder, retryCount + 1);
      }, 150);
    } else {
      console.error('Form not ready after maximum retries');
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: 'Form initialization timeout. Please refresh the page.',
      });
    }
  }

  private fillFormFromTO(transferOrder: TransferOrderOption): void {
    const form = this.addFormComponent?.form;
    if (!form) {
      return;
    }

    // Set outType to 'transfer-out-stock' (value for transfer_order)
    const outTypeControl = form.get('outType');
    if (outTypeControl) {
      outTypeControl.setValue('transfer-out-stock', { emitEvent: false });
    }

    // Set frontRecordCode value (already set via defaultValue in field config, but set again to be sure)
    const frontRecordCodeControl = form.get('frontRecordCode');
    if (frontRecordCodeControl && transferOrder.code) {
      frontRecordCodeControl.setValue(transferOrder.code, { emitEvent: false });
      frontRecordCodeControl.disable({ emitEvent: false });
    }

    // Load TO details to fill warehouse and items
    this.loadTransferOrderDetails(transferOrder.id);
  }


  onSave(): void {
    if (this.loading()) {
      return;
    }

    if (!this.addFormComponent) {
      return;
    }

    const form = this.addFormComponent.form;
    
    // Mark all fields as touched to trigger validation
    if (form) {
      Object.keys(form.controls).forEach((key) => {
        form.get(key)?.markAsTouched();
      });
    }
    
    if (!form || !form.valid) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('common.messages.form_invalid'),
      });
      return;
    }

    const formValue = form.getRawValue();
    
    // Form values are already in API format, use directly
    // Valid values: 'transfer-out-stock', 'order-sales-out-stock', 'purchase-order-return-out-stock'
    const validOutboundTypes = ['transfer-out-stock', 'order-sales-out-stock', 'purchase-order-return-out-stock'];
    const outboundType = formValue.outType || this.currentOutType();
    
    if (!outboundType || !validOutboundTypes.includes(outboundType)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('outbound_management.messages.invalid_outbound_type'),
      });
      return;
    }

    // orderCode is the reference document (chứng từ gốc)
    // Always use frontRecordCode as orderCode
    const frontRecordCodeControl = form.get('frontRecordCode');
    const orderCode = formValue.frontRecordCode || frontRecordCodeControl?.value;

    if (!orderCode) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('outbound_management.messages.order_code_required'),
      });
      return;
    }

    // Build API request body
    const createDTO = {
      orderCode: orderCode,
      outboundType: outboundType,
      logisticCompany: formValue.logisticCompany,
      logisticCompanyCode: formValue.logisticCompanyCode,
      logisticCompanyId: formValue.logisticCompanyId 
        ? (typeof formValue.logisticCompanyId === 'string' 
            ? parseInt(formValue.logisticCompanyId, 10) 
            : formValue.logisticCompanyId)
        : undefined,
      logisticBusiness: formValue.logisticBusiness,
      logisticBusinessCode: formValue.logisticBusinessCode,
      logisticBusinessId: formValue.logisticBusinessId
        ? (typeof formValue.logisticBusinessId === 'string' 
            ? parseInt(formValue.logisticBusinessId, 10) 
            : formValue.logisticBusinessId)
        : undefined,
      scheduledDeliveryTime: formValue.scheduledDeliveryTime,
      orderMsg: formValue.orderMsg || formValue.description,
    };

    this.loading.set(true);
    
    this.stockDeliveryService.createOutboundDeliveryOrder(createDTO)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant('outbound_management.messages.created_successfully'),
          });
          
          // Navigate back to list page
          this.router.navigate(['/stock/delivery/delivery-order']).catch((error) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.error'),
              detail: 'Failed to navigate to list page',
            });
          });
        },
        error: (error) => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('outbound_management.messages.failed_to_create'),
          });
        },
      });
  }

  onCancel(): void {
    this.router.navigate(['/stock/delivery/delivery-order']);
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  // ============================================================================
  // PURCHASE RETURN ORDER METHODS
  // ============================================================================

  private updateFormFieldsForPurchaseReturnOrder(disableFrontRecordCode: boolean = false, porCode?: string): void {
    // Update frontRecordCode field to show and optionally disable
    const fields = this.formFields().map((field) => {
      if (field.controlName === 'frontRecordCode') {
        if (disableFrontRecordCode && porCode) {
          // When creating from POR, change to text type (readonly)
          return { 
            ...field, 
            hidden: false, 
            type: 'text' as const,
            disabled: true,
            readonly: true,
            defaultValue: porCode,
          };
        } else {
          // Normal mode - keep as searchableDropdown
          // Use already loaded POR options if available
          const porOptions = this.porOptionsList();
          return { 
            ...field, 
            hidden: false, 
            disabled: false,
            readonly: false,
            type: 'searchableDropdown' as const,
            options: porOptions.length > 0 ? porOptions : field.options,
          };
        }
      }
      return field;
    });
    this.formFields.set(fields);
    
    // No need to load POR options here - already loaded in ngOnInit
  }

  private loadPOROptions(): void {
    // Skip if already loaded
    if (this.porOptionsLoaded()) {
      return;
    }

    // Mark as loading to prevent duplicate calls
    this.porOptionsLoaded.set(true);

    // Load PORs with status APPROVED and PARTIALLY_SHIPPED
    // Note: API uses 0-based page index, but we pass page: 0 which should be correct
    this.porService.getPORList({
      page: 0,
      size: 100,
      sortBy: 'creationDate',
      ascending: false,
      status: ['APPROVED', 'PARTIALLY_SHIPPED'],
    }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const porOptions = (response.data || []).map((por) => ({
            label: por.porNo || por.rtvNo || '',
            value: por.porNo || por.rtvNo || '',
            id: por.id,
          }));
          // Store POR options for quick access when switching outType
          this.porOptionsList.set(porOptions);
          // Update form field options if POR outType is currently selected
          if (this.currentOutType() === 'purchase-order-return-out-stock') {
            this.updateFrontRecordCodeFieldOptions(porOptions);
          }
        },
        error: (error) => {
          // Reset loaded flag on error so it can be retried
          this.porOptionsLoaded.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('outbound_management.messages.failed_to_load_por'),
          });
        },
      });
  }

  private loadPORAndFillForm(porId: number): void {
    this.loading.set(true);
    this.porService.getPORDetailWithItems(porId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ por, items }) => {
          // Reset loading flag after successful load
          this.isLoadingPORDetails = false;
          // Fill form with POR data
          if (this.addFormComponent?.form) {
            const form = this.addFormComponent.form;
            
            // Set outType to match dropdown option value
            form.get('outType')?.setValue('purchase-order-return-out-stock', { emitEvent: false });
            // Also update currentOutType signal
            this.currentOutType.set('purchase-order-return-out-stock');
            
            // Set frontRecordCode (POR code)
            form.get('frontRecordCode')?.setValue(por.porNo || por.rtvNo || '', { emitEvent: false });
            
            // Set outbound warehouse (from POR)
            form.get('outLogicWarehouseCode')?.setValue(por.warehouseCode || '', { emitEvent: false });
            
            // Set receiving warehouse value to vendor name
            const receivingWarehouseValue = por.vendorName || '';
            form.get('receivingLogicWarehouseCode')?.setValue(receivingWarehouseValue, { emitEvent: false });
            
            // Map POR items to OutboundDeliveryItem format
            const deliveryItems: OutboundDeliveryItem[] = items.map((item, index) => ({
              id: `por-item-${item.id || index}`,
              serialNumber: index + 1,
              spuCode: item.spuCode || '',
              spuName: item.spuName || '',
              skuCode: item.skuCode || '',
              skuName: item.skuName || '',
              quantity: item.returnQty || 0,
              plannedQty: item.returnQty || 0,
              pickedQty: 0,
            }));
            
            this.productList.set(deliveryItems);
            this.updateTableSections();
          }
          
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading POR detail:', error);
          this.loading.set(false);
          this.isLoadingPORDetails = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('outbound_management.messages.failed_to_load_por'),
          });
        },
      });
  }

  private handlePORSelection(porCode: string): void {
    // Prevent duplicate calls
    if (this.isLoadingPORDetails) {
      return;
    }
    
    // Update last selected POR code
    this.lastSelectedPORCode = porCode;
    
    // First, try to find POR ID from already loaded options
    const porOptions = this.porOptionsList();
    const porOption = porOptions.find(p => p.value === porCode);
    
    if (porOption && porOption.id) {
      // Found in options, use it directly without API call
      this.loadPORAndFillForm(porOption.id);
      return;
    }
    
    // If not found in options, call API to find POR
    this.isLoadingPORDetails = true;
    this.loading.set(true);
    
    // Get POR list to find the ID
    this.porService.getPORList({
      page: 0,
      size: 100,
      code: porCode,
    }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const por = (response.data || []).find(p => (p.porNo || p.rtvNo) === porCode);
          if (por) {
            this.loadPORAndFillForm(por.id);
          } else {
            // Only show warning if POR code was actually entered by user
            // Don't show warning if it's just a form value change during outType switch
            this.loading.set(false);
            this.isLoadingPORDetails = false;
          }
        },
        error: (error) => {
          console.error('Error finding POR:', error);
          this.loading.set(false);
          this.isLoadingPORDetails = false;
        },
      });
  }

  private clearPORData(): void {
    if (this.addFormComponent?.form) {
      const form = this.addFormComponent.form;
      // Clear warehouse codes
      const outWarehouseCodeControl = form.get('outLogicWarehouseCode');
      if (outWarehouseCodeControl) {
        outWarehouseCodeControl.setValue(null, { emitEvent: false });
      }
      const receivingWarehouseCodeControl = form.get('receivingLogicWarehouseCode');
      if (receivingWarehouseCodeControl) {
        receivingWarehouseCodeControl.setValue(null, { emitEvent: false });
      }
    }
    // Clear product list
    this.productList.set([]);
    this.updateTableSections();
  }
}

